import { useState, useEffect } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Badge } from "../../ui/badge";
import { ScrollArea } from "../../ui/scroll-area";
import { Search, FileText, Loader2, X } from "lucide-react";
import { listResources } from "../../../lib/api";
import { getMatiereInfo } from "../constants";

export function ResourceSelector({ selectedResourceId, onResourceChange }) {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      const { data } = await listResources({ limit: 50 });
      setResources(data);
    } catch (err) {
      console.error("Error fetching resources:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredResources = resources.filter((r) =>
    r.title.toLowerCase().includes(search.toLowerCase())
  );

  const selectedResource = resources.find((r) => r.id === selectedResourceId);

  return (
    <div className="space-y-3">
      {selectedResource && (
        <div className="flex items-center gap-2 p-2 bg-indigo-50 border border-indigo-200 rounded-lg">
          <FileText className="w-4 h-4 text-indigo-600" />
          <span className="flex-1 text-sm font-medium truncate">{selectedResource.title}</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={() => onResourceChange(null, null)}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Rechercher une ressource..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-8 h-9"
        />
      </div>

      <ScrollArea className="h-[200px] border rounded-lg">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : filteredResources.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-slate-400 p-4">
            <FileText className="w-8 h-8 mb-2" />
            <p>Aucune ressource trouvee</p>
          </div>
        ) : (
          <div className="p-2 space-y-1">
            {filteredResources.map((resource) => {
              const matiereInfo = getMatiereInfo(resource.matiere);
              return (
                <div
                  key={resource.id}
                  className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                    selectedResourceId === resource.id
                      ? "bg-indigo-50 border border-indigo-200"
                      : "hover:bg-slate-50"
                  }`}
                  onClick={() => onResourceChange(resource.id, resource.title)}
                >
                  <FileText className="w-4 h-4 text-slate-400" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{resource.title}</p>
                    <div className="flex gap-1 mt-0.5">
                      {resource.niveau && (
                        <Badge variant="outline" className="text-[10px] px-1">
                          {resource.niveau.toUpperCase()}
                        </Badge>
                      )}
                      {matiereInfo && (
                        <Badge
                          variant="outline"
                          className="text-[10px] px-1"
                          style={{ borderColor: matiereInfo.color, color: matiereInfo.color }}
                        >
                          {matiereInfo.label}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}

export default ResourceSelector;
